import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { Center, Column, Screen, StepperIndicator, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BackButton } from '~/commonComponents';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog'; // cspell:ignore posthog
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { useProfessionOptions } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireDraftReminderEmailLinkClicked } from '~/posthogs/events';
import { NumberUtils } from '~/utils/NumberUtils';
import { pluralize } from '~/utils/String';
import { formatProfessionalCount } from '../utils';
import {
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageLocationState,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import { useCurrentPermanentJobPostingSlug, useDraftJobPost } from './hooks';

export const CreatePermanentJobsPage = () => {
  const location = useLocation();
  const slug = useCurrentPermanentJobPostingSlug();
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const navigate = useNavigate();

  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const { draft, isLoading } = useDraftJobPost(id);

  const user = useUserSelector();
  const {
    countsByProfession,
    isLoading: isCountLoading,
    isError: isCountError,
  } = useGetProfessionalCountsByOffice(user?.id, {
    enable: isEnforcePostAJobFunnelImprovement,
    distance: 25,
  });

  const { findByCode } = useProfessionOptions();
  const professionType = draft?.professionType ?? '';
  const professionTypeLabel = findByCode(professionType)?.label ?? '';
  const totalCount = countsByProfession
    ? (countsByProfession[professionType as keyof typeof countsByProfession] ?? 0)
    : 0;
  const showCount =
    isEnforcePostAJobFunnelImprovement && !isCountLoading && !isCountError && totalCount > 0;

  useEffect(() => {
    if (isLoading || !draft) return;

    if (draft.status !== JobPostingStatus.Draft) {
      navigate('/', { replace: true });
    }
  }, [draft, isLoading, navigate]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('utm_source') === 'email' && params.get('utm_medium') === 'draft_reminder') {
      fireDraftReminderEmailLinkClicked({ tier: params.get('utm_content') ?? undefined });
    }
  }, [location.search]);

  // Review & Payment is shared with the Copy & Paste import flow (Import → Confirm
  // Details → Payment). When entered from there, show that 3-step stepper instead of
  // the manual 4-step one.
  const isFromImport = Boolean(
    (location.state as TCreatePermanentJobPostingPageLocationState | null)?.fromImport,
  );

  const steps = useMemo(() => {
    if (isFromImport) {
      return ['Import', 'Confirm Details', 'Payment'];
    }
    if (isEnforcePostAJobFunnelImprovement) {
      return ['Job Information', 'Work Details', 'Description', 'Payment'];
    }
    return ['Job Details', 'Payment'];
  }, [isEnforcePostAJobFunnelImprovement, isFromImport]);

  const currentStepIndex = useMemo(() => {
    if (isFromImport) {
      // Import (0) → Confirm Details (1) → Payment (2)
      return 2;
    }
    if (isEnforcePostAJobFunnelImprovement) {
      switch (slug) {
        case CreatePermanentJobPostingSteps.BasicInformation:
          return 0;
        case CreatePermanentJobPostingSteps.WorkDetails:
          return 1;
        case CreatePermanentJobPostingSteps.JobDescription:
          return 2;
        case CreatePermanentJobPostingSteps.ReviewAndPayment:
          return 3;
        default:
          return 0;
      }
    }

    if (slug === CreatePermanentJobPostingSteps.ReviewAndPayment) {
      return 1;
    }

    return 0;
  }, [slug, isEnforcePostAJobFunnelImprovement, isFromImport]);

  // The funnel header is contextual: step 1 asks what they're hiring for, then once
  // the professional count is known it flips to a count-led nudge to finish the post.
  const header = useMemo<{ title?: string; subtitle?: string }>(() => {
    if (!isEnforcePostAJobFunnelImprovement) {
      return { title: 'Post a Job' };
    }

    const countLabel = pluralize(professionTypeLabel, totalCount);

    // Indexed by step (0–3), matching currentStepIndex.
    const headersByStep: { title?: string; subtitle?: string }[] = [
      // 0 · Job Information
      {
        title: "Let's See Who's In Your Area",
        subtitle:
          "Tell us what you're hiring for and we'll show you how many professionals are near you.",
      },
      // 1 · Work Details — once the count is known, lead with it
      {
        title: showCount
          ? `${NumberUtils.applyThousandSeparator(totalCount)} ${countLabel} in Your Area`
          : 'Dental Professionals Near You Are on GoTu',
        subtitle:
          "Just set your schedule and compensation and we'll get your job in front of them.",
      },
      // 2 · Job Description
      {
        title: 'One More Step to Receive Applicants',
        subtitle: showCount
          ? // eslint-disable-next-line max-len
            `Add your job description and your role goes out to ${formatProfessionalCount(totalCount)} ${pluralize(professionTypeLabel.toLowerCase(), totalCount)}.`
          : // eslint-disable-next-line max-len
            `Add your job description and your role goes out to ${professionTypeLabel.toLowerCase()} in your area.`,
      },
      // 3 · Review & Payment
      {
        title: "Now Let's Tell Them You're Hiring",
        subtitle: 'Once you post, qualified professionals are notified and can apply immediately.',
      },
    ];

    return headersByStep[currentStepIndex] ?? {};
  }, [
    isEnforcePostAJobFunnelImprovement,
    currentStepIndex,
    showCount,
    totalCount,
    professionTypeLabel,
  ]);

  return (
    <Screen variant="standard">
      <Screen.Header
        start={<BackButton />}
        title={<StepperIndicator steps={steps} currentStep={currentStepIndex} />}
      >
        {(header.title || header.subtitle) && (
          <Center>
            <Column align="center" gap={2} style={{ maxWidth: 633 }}>
              {header.title && (
                <Typography
                  as="span"
                  bold
                  size="heading_2"
                  color={colors.neutral[600]}
                  textAlign="center"
                >
                  {header.title}
                </Typography>
              )}
              {header.subtitle && (
                <Typography
                  as="span"
                  size="body_large"
                  color={colors.neutral[500]}
                  textAlign="center"
                >
                  {header.subtitle}
                </Typography>
              )}
            </Column>
          </Center>
        )}
      </Screen.Header>
      <Screen.Body>
        <Column gap={0} style={{ paddingLeft: 28, paddingRight: 28, paddingBottom: 28 }}>
          <Outlet />
        </Column>
      </Screen.Body>
    </Screen>
  );
};
